import React, { useState } from 'react';
import { X, MapPin, Loader2 } from 'lucide-react';

interface AddressModalProps {
  contractId: number;
  onClose: () => void;
  onSuccess: () => void;
}

export function AddressModal({ contractId, onClose, onSuccess }: AddressModalProps) {
  const [street, setStreet] = useState('');
  const [postcode, setPostcode] = useState('');
  const [city, setCity] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (!street.trim() || !postcode.trim() || !city.trim()) {
      setError('Bitte füllen Sie alle Felder aus');
      return;
    }

    if (street.length > 255) {
      setError('Straße darf maximal 255 Zeichen lang sein');
      return;
    }

    if (postcode.length > 10) {
      setError('Postleitzahl darf maximal 10 Zeichen lang sein');
      return;
    }

    if (city.length > 255) {
      setError('Stadt darf maximal 255 Zeichen lang sein');
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('accessToken');
      if (!token) {
        throw new Error('Keine Authentifizierung verfügbar');
      }

      const response = await fetch(
        `${import.meta.env.VITE_API_URL || 'http://localhost:3001/api'}/contracts/${contractId}/update-address`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            street: street.trim(),
            postcode: postcode.trim(),
            city: city.trim(),
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Fehler beim Speichern der Adresse');
      }

      // Success! Call onSuccess to trigger download
      onSuccess();
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Ein unerwarteter Fehler ist aufgetreten');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl max-w-md w-full p-6"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <MapPin size={20} className="text-blue-600" />
            </div>
            <h3 className="text-xl font-semibold text-[var(--ucm-text-dark)]">
              Adresse für Vertrag
            </h3>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            disabled={loading}
          >
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        {/* Info Message */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <p className="text-sm text-blue-800">
            Bitte geben Sie Ihre Adresse ein. Diese wird für die Erstellung Ihres Arbeitsvertrags benötigt.
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Street Input */}
          <div>
            <label className="block text-sm font-medium text-[var(--ucm-text-dark)] mb-2">
              Straße und Hausnummer *
            </label>
            <input
              type="text"
              value={street}
              onChange={(e) => setStreet(e.target.value)}
              placeholder="z.B. Musterstraße 123"
              maxLength={255}
              disabled={loading}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
            />
          </div>

          {/* Postcode Input */}
          <div>
            <label className="block text-sm font-medium text-[var(--ucm-text-dark)] mb-2">
              Postleitzahl *
            </label>
            <input
              type="text"
              value={postcode}
              onChange={(e) => setPostcode(e.target.value)}
              placeholder="z.B. 12345"
              maxLength={10}
              disabled={loading}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
            />
          </div>

          {/* City Input */}
          <div>
            <label className="block text-sm font-medium text-[var(--ucm-text-dark)] mb-2">
              Stadt *
            </label>
            <input
              type="text"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              placeholder="z.B. Frankfurt"
              maxLength={255}
              disabled={loading}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
            />
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* Buttons */}
          <div className="flex space-x-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="flex-1 h-12 bg-gray-100 text-gray-700 font-semibold rounded-full hover:bg-gray-200 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 h-12 bg-blue-600 text-white font-semibold rounded-full hover:bg-blue-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
            >
              {loading ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  <span>Speichert...</span>
                </>
              ) : (
                <span>Speichern</span>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
